import {
  Controller,
  useWatch,
  type Control,
  type FieldPath,
  type FieldValues
} from 'react-hook-form';
import { useFieldErrorMessage } from './useFieldErrorMessage';
import { FormFieldSystemSelect } from '../FormFieldSystemSelect';

type SystemSelectFilter = React.ComponentProps<typeof FormFieldSystemSelect>['filter'];

export interface FormSystemSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  /**
   * React Hook Form control object
   */
  control: Control<TFieldValues>;

  /**
   * Field name in the form
   */
  name: TName;

  /**
   * Field label
   */
  label?: string;

  /**
   * Field description/helper text
   */
  description?: string;

  /**
   * Mark field as required
   */
  required?: boolean;

  /**
   * Placeholder text
   */
  placeholder?: string;

  /**
   * Whether the select is disabled
   */
  disabled?: boolean;

  /**
   * Field orientation
   */
  orientation?: 'vertical' | 'horizontal' | 'responsive';

  /**
   * Additional class for Field wrapper
   */
  fieldClassName?: string;

  /**
   * Additional class for SelectTrigger
   */
  className?: string;

  /**
   * Default value for the field
   */
  defaultValue?: string;

  /**
   * Allow clearing the selection (useful for optional fields)
   */
  allowClear?: boolean;

  /**
   * Custom label for the clear/all option
   */
  clearLabel?: string;

  /**
   * Optional callback for custom clear behavior
   */
  onClear?: () => void;

  /**
   * Client-side filter function to apply before internal search filter
   */
  filter?: SystemSelectFilter;
}

/**
 * React Hook Form adapter for SystemSelect with Field integration
 * Automatically fetches systems and handles form state, validation, and errors
 *
 * @example
 * ```tsx
 * const { control } = useForm<FormData>();
 *
 * <FormSystemSelect
 *   control={control}
 *   name="systemId"
 *   label="Software System"
 *   required
 * />
 * ```
 */
export function FormSystemSelect<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  control,
  name,
  label,
  description,
  required = false,
  placeholder,
  disabled = false,
  orientation = 'vertical',
  fieldClassName,
  className,
  defaultValue,
  allowClear = false,
  clearLabel,
  onClear,
  filter
}: FormSystemSelectProps<TFieldValues, TName>) {
  const fieldErrorMessage = useFieldErrorMessage();

  // Display from the raw watched value, not Controller's field.value, which
  // masks a cleared (undefined) value back to the field's original value. See
  // FormUserSelect for the full explanation.
  const value = useWatch({ control, name });

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue as never}
      render={({ field, fieldState }) => (
        <FormFieldSystemSelect
          id={String(name)}
          name={String(name)}
          label={label}
          description={description}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          error={fieldErrorMessage(fieldState.error)}
          orientation={orientation}
          fieldClassName={fieldClassName}
          className={className}
          allowClear={allowClear}
          clearLabel={clearLabel}
          onClear={onClear}
          filter={filter}
          value={value as string | undefined}
          onChange={field.onChange}
          ref={field.ref}
        />
      )}
    />
  );
}

FormSystemSelect.displayName = 'FormSystemSelect';
